/* eslint-disable */
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Star, ChevronLeft, ChevronRight, X, MessageSquare } from 'lucide-react';

const C = {
  cream: '#F7F6EF', charcoal: '#2F3A42', navy: '#445366',
  mid: '#7A8A96', border: '#D8DEE3',
  skyBlue: '#BCD9E5', skyBlueLight: '#EAF4F8', skyText: '#3A8FA3',
  green: '#7EA594', greenLight: '#EAF2EF',
  orange: '#E4AC49', orangeLight: '#FDF3DC',
  amber: '#E07B00', amberLight: '#FFF3E0',
  red: '#DC3545', redLight: '#FFF0F0',
  white: '#FFFFFF',
};

const VENUE_IDENTITY = {
  'Box Jordaan':  { bg: '#EDF3FC', text: '#2563EB', border: '#BAD4F5', short: 'Jordaan'  },
  'Box Plantage': { bg: '#C8E6D8', text: '#2D7A65', border: '#7EA594', short: 'Plantage' },
  'Sassys':       { bg: '#EDE0F7', text: '#6D28A0', border: '#6D28A0', short: 'Sassys'   },
};

const VENUES = ['Box Jordaan', 'Box Plantage', 'Sassys'];

const MONTHS_LONG  = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAY_NAMES    = ['Mo','Tu','We','Th','Fr','Sa','Su'];

// ── Helpers ────────────────────────────────────────────────────────────────────
function getDaysInMonth(y, m)  { return new Date(y, m + 1, 0).getDate(); }
function getFirstDayOfMonth(y, m) {
  let d = new Date(y, m, 1).getDay();
  return d === 0 ? 6 : d - 1; // Mon-first
}

function relativeDate(dateStr) {
  const now  = new Date();
  const then = new Date(dateStr);
  const diff = Math.floor((now - then) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7)  return `${diff} days ago`;
  if (diff < 30) return `${Math.floor(diff / 7)}w ago`;
  if (diff < 365) return `${Math.floor(diff / 30)}mo ago`;
  return `${Math.floor(diff / 365)}y ago`;
}

function isoWeek(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

// ── Star renderer ──────────────────────────────────────────────────────────────
function Stars({ rating, size = 13 }) {
  return (
    <div style={{ display: 'flex', gap: '1px' }}>
      {[1,2,3,4,5].map(i => (
        <Star
          key={i}
          size={size}
          fill={i <= rating ? '#F7EC71' : 'none'}
          stroke={i <= rating ? '#E4AC49' : C.border}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

// ── Rating colour for calendar cells ──────────────────────────────────────────
function ratingBg(avg) {
  if (avg === null) return null;
  if (avg >= 4)    return { bg: C.greenLight,  text: C.green  };
  if (avg >= 2)    return { bg: C.amberLight,  text: C.amber  };
  return               { bg: C.redLight,    text: C.red    };
}

// ── Venue card chart data ──────────────────────────────────────────────────────
// Builds monthly 5★ counts Jan–Dec for CY and LY
function buildMonthlyData(reviews, now) {
  const cy = now.getFullYear();
  const ly = cy - 1;
  return MONTHS_SHORT.map((label, m) => {
    const cyPrefix = `${cy}-${String(m + 1).padStart(2, '0')}`;
    const lyPrefix = `${ly}-${String(m + 1).padStart(2, '0')}`;
    const cyFive = reviews.filter(r => r.review_date.startsWith(cyPrefix) && r.star_rating === 5).length;
    const lyFive = reviews.filter(r => r.review_date.startsWith(lyPrefix) && r.star_rating === 5).length;
    // Don't plot future CY months
    const isFuture = m > now.getMonth();
    return { label, cy: isFuture ? null : cyFive, ly: lyFive };
  });
}

// ── Monthly summary for modal table ───────────────────────────────────────────
function buildMonthlyTable(reviews, now) {
  const cy = now.getFullYear();
  const ly = cy - 1;
  return MONTHS_SHORT.map((label, m) => {
    const cyPrefix = `${cy}-${String(m + 1).padStart(2, '0')}`;
    const lyPrefix = `${ly}-${String(m + 1).padStart(2, '0')}`;
    const cyAll  = reviews.filter(r => r.review_date.startsWith(cyPrefix));
    const lyAll  = reviews.filter(r => r.review_date.startsWith(lyPrefix));
    const cyFive = cyAll.filter(r => r.star_rating === 5).length;
    const lyFive = lyAll.filter(r => r.star_rating === 5).length;
    const cyAvg  = cyAll.length > 0 ? (Math.floor(cyAll.reduce((s,r) => s + r.star_rating, 0) / cyAll.length * 10) / 10).toFixed(1) : null;
    const lyAvg  = lyAll.length > 0 ? (Math.floor(lyAll.reduce((s,r) => s + r.star_rating, 0) / lyAll.length * 10) / 10).toFixed(1) : null;
    const isFuture = m > now.getMonth();
    return { label, m, cyFive, lyFive, cyAvg, lyAvg, cyTotal: cyAll.length, lyTotal: lyAll.length, isFuture };
  });
}

// ── Review card ────────────────────────────────────────────────────────────────
function ReviewCard({ review, compact = false }) {
  const [expanded, setExpanded] = useState(false);
  const vi = VENUE_IDENTITY[review.venue] || {};
  const hasComment = review.comment && review.comment.trim().length > 0;
  const long = hasComment && review.comment.trim().length > 200;

  return (
    <div style={{
      background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px',
      padding: compact ? '10px 12px' : '12px 14px', marginBottom: '8px',
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <Stars rating={review.star_rating} size={12} />
            {!compact && (
              <span style={{ fontSize: '11px', padding: '1px 7px', borderRadius: '20px', background: vi.bg, color: vi.text, fontWeight: 700, border: `1px solid ${vi.border}` }}>
                {vi.short}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: C.charcoal }}>
              {review.reviewer_name || 'Anonymous'}
            </span>
            <span style={{ fontSize: '11px', color: C.mid }}>
              · {relativeDate(review.review_date)}
            </span>
          </div>
        </div>
      </div>

      {/* Comment */}
      {hasComment && (
        <div style={{ fontSize: '12px', color: C.charcoal, lineHeight: 1.55 }}>
          {(!long || expanded) ? review.comment.trim() : review.comment.trim().slice(0, 200) + '…'}
          {long && (
            <button
              onClick={() => setExpanded(p => !p)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.skyText, fontSize: '11px', fontWeight: 700, padding: '0 0 0 4px' }}
            >
              {expanded ? 'Show less' : 'Read more'}
            </button>
          )}
        </div>
      )}

      {/* Reply status */}
      {review.reply && (
        <div style={{ marginTop: '6px' }}>
          <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px', background: C.greenLight, color: C.green, border: `1px solid ${C.green}33` }}>
            ✓ Replied
          </span>
        </div>
      )}
    </div>
  );
}

// ── Venue detail modal — calendar + review list ────────────────────────────────
function VenueModal({ venue, reviews, onClose }) {
  const vi = VENUE_IDENTITY[venue] || {};
  const now = new Date();

  const [year,        setYear]        = useState(now.getFullYear());
  const [month,       setMonth]       = useState(now.getMonth());
  const [selectedDay, setSelectedDay] = useState(null);
  const [starFilter,  setStarFilter]  = useState(null); // null = all

  const prevMonth = () => {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
    setSelectedDay(null);
  };
  const nextMonth = () => {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
    setSelectedDay(null);
  };

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay    = getFirstDayOfMonth(year, month);
  const totalCells  = Math.ceil((daysInMonth + firstDay) / 7) * 7;
  const todayStr    = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;

  // Reviews for this month
  const monthReviews = reviews.filter(r => {
    const d = new Date(r.review_date);
    return d.getFullYear() === year && d.getMonth() === month;
  });

  // Avg rating per day
  const dailyAvg = {};
  monthReviews.forEach(r => {
    const ds = r.review_date.slice(0, 10);
    if (!dailyAvg[ds]) dailyAvg[ds] = [];
    dailyAvg[ds].push(r.star_rating);
  });
  const dailyAvgRating = {};
  Object.entries(dailyAvg).forEach(([ds, arr]) => {
    dailyAvgRating[ds] = arr.reduce((a,b) => a+b, 0) / arr.length;
  });

  // Reviews below calendar
  const selectedDateStr = selectedDay
    ? `${year}-${String(month+1).padStart(2,'0')}-${String(selectedDay).padStart(2,'0')}`
    : null;

  const displayReviews = monthReviews.filter(r => {
    if (selectedDay && r.review_date.slice(0,10) !== selectedDateStr) return false;
    if (starFilter !== null && r.star_rating !== starFilter) return false;
    return true;
  });

  // Star count buttons for filter
  const starCounts = [5,4,3,2,1].map(s => ({
    star: s,
    count: monthReviews.filter(r => r.star_rating === s).length,
  }));

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 10001, background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: C.cream, borderRadius: '16px', width: '100%', maxWidth: '560px', maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.28)' }}>

        {/* Header */}
        <div style={{ background: C.navy, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 9px', borderRadius: '20px', background: vi.bg, color: vi.text, border: `1px solid ${vi.border}` }}>
              {venue}
            </span>
            <div style={{ fontWeight: 800, fontSize: '16px', color: C.white, marginTop: '5px' }}>Reviews</div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: C.white, padding: '6px 9px', fontSize: '14px' }}>✕</button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>

          {/* Monthly summary table */}
          {(() => {
            const tableData = buildMonthlyTable(reviews, new Date());
            const now = new Date();
            return (
              <div style={{ padding: '12px 14px 0' }}>
                <div style={{ fontSize: '10px', fontWeight: 800, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                  Monthly Summary
                </div>
                <div style={{ background: C.white, borderRadius: '10px', border: `1px solid ${C.border}`, overflow: 'hidden', marginBottom: '12px' }}>
                  {/* Header */}
                  <div style={{ display: 'grid', gridTemplateColumns: '36px 1fr 1fr 4px 1fr 1fr', background: C.skyBlueLight, borderBottom: `1px solid ${C.skyBlue}` }}>
                    <div style={{ padding: '6px 4px 6px 8px', fontSize: '9px', fontWeight: 700, color: C.skyText }} />
                    <div style={{ padding: '6px 4px', textAlign: 'center', fontSize: '9px', fontWeight: 700, color: C.skyText, textTransform: 'uppercase', letterSpacing: '0.05em' }}>5★</div>
                    <div style={{ padding: '6px 4px', textAlign: 'center', fontSize: '9px', fontWeight: 700, color: C.skyText, textTransform: 'uppercase', letterSpacing: '0.05em' }}>AVG</div>
                    <div style={{ background: C.border }} />
                    <div style={{ padding: '6px 4px', textAlign: 'center', fontSize: '9px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>LY 5★</div>
                    <div style={{ padding: '6px 4px', textAlign: 'center', fontSize: '9px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>LY AVG</div>
                  </div>
                  {/* Rows — all 12 months, scrollable */}
                  <div style={{ maxHeight: '192px', overflowY: 'auto' }}>
                    {tableData.map((row, i) => (
                      <div
                        key={row.label}
                        onClick={() => {
                          if (!row.isFuture) { setYear(now.getFullYear()); setMonth(row.m); setSelectedDay(null); }
                        }}
                        style={{
                          display: 'grid', gridTemplateColumns: '36px 1fr 1fr 4px 1fr 1fr',
                          borderBottom: i < 11 ? `1px solid ${C.border}` : 'none',
                          cursor: row.isFuture ? 'default' : 'pointer',
                          background: month === row.m && year === now.getFullYear() ? '#F0F7FF' : 'transparent',
                          opacity: row.isFuture ? 0.4 : 1,
                        }}
                      >
                        <div style={{ padding: '7px 4px 7px 8px', fontSize: '11px', fontWeight: 700, color: C.charcoal }}>{row.label}</div>
                        <div style={{ padding: '7px 4px', textAlign: 'center', fontSize: '11px', fontWeight: 800, color: row.isFuture ? C.mid : C.amber }}>{row.cyFive || '—'}</div>
                        <div style={{ padding: '7px 4px', textAlign: 'center', fontSize: '11px', fontWeight: 600, color: C.charcoal }}>{row.cyAvg ?? '—'}</div>
                        <div style={{ background: C.border }} />
                        <div style={{ padding: '7px 4px', textAlign: 'center', fontSize: '11px', fontWeight: 600, color: C.mid }}>{row.lyFive || '—'}</div>
                        <div style={{ padding: '7px 4px', textAlign: 'center', fontSize: '11px', fontWeight: 600, color: C.mid }}>{row.lyAvg ?? '—'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Month nav */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px 8px' }}>
            <button onClick={prevMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.navy, padding: '4px 8px', borderRadius: '6px' }}>
              <ChevronLeft size={16} />
            </button>
            <span style={{ fontSize: '14px', fontWeight: 800, color: C.charcoal }}>
              {MONTHS_LONG[month]} {year}
            </span>
            <button
              onClick={nextMonth}
              disabled={year === now.getFullYear() && month === now.getMonth()}
              style={{ background: 'none', border: 'none', cursor: year === now.getFullYear() && month === now.getMonth() ? 'default' : 'pointer', color: year === now.getFullYear() && month === now.getMonth() ? C.border : C.navy, padding: '4px 8px', borderRadius: '6px' }}
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Calendar grid */}
          <div style={{ padding: '0 12px 8px' }}>
            <div style={{ background: C.white, borderRadius: '12px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
              {/* Day headers */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', background: C.skyBlueLight, borderBottom: `1px solid ${C.skyBlue}` }}>
                {DAY_NAMES.map(d => (
                  <div key={d} style={{ padding: '8px 0', textAlign: 'center', fontSize: '10px', fontWeight: 700, color: C.skyText, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    {d}
                  </div>
                ))}
              </div>
              {/* Cells */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '4px 2px 8px' }}>
                {Array.from({ length: totalCells }).map((_, idx) => {
                  const dayNum = idx - firstDay + 1;
                  const isCurrentMonth = dayNum >= 1 && dayNum <= daysInMonth;
                  const ds = isCurrentMonth
                    ? `${year}-${String(month+1).padStart(2,'0')}-${String(dayNum).padStart(2,'0')}`
                    : null;
                  const isToday    = ds === todayStr;
                  const isSelected = isCurrentMonth && dayNum === selectedDay;
                  const avg        = ds ? dailyAvgRating[ds] ?? null : null;
                  const hasReviews = avg !== null;
                  const colour     = ratingBg(avg);

                  return (
                    <div
                      key={idx}
                      onClick={() => {
                        if (!isCurrentMonth) return;
                        setSelectedDay(dayNum === selectedDay ? null : dayNum);
                        setStarFilter(null);
                      }}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', padding: '3px 1px', cursor: isCurrentMonth ? 'pointer' : 'default' }}
                    >
                      <div style={{
                        width: '30px', height: '30px', borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: isSelected
                          ? C.navy
                          : isToday
                            ? C.skyBlue
                            : hasReviews
                              ? colour.bg
                              : 'transparent',
                        border: isSelected ? `2px solid ${C.navy}` : '2px solid transparent',
                        transition: 'all 0.12s',
                      }}>
                        <span style={{
                          fontSize: '12px',
                          fontWeight: isToday || isSelected ? 800 : hasReviews ? 700 : 400,
                          color: isSelected
                            ? C.white
                            : isToday
                              ? C.navy
                              : hasReviews
                                ? colour.text
                                : isCurrentMonth
                                  ? C.charcoal
                                  : '#C8D4DC',
                        }}>
                          {isCurrentMonth ? dayNum : ''}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '8px', paddingLeft: '2px' }}>
              {[{bg: C.greenLight, text: C.green, label: '4–5 ★'}, {bg: C.amberLight, text: C.amber, label: '2–3 ★'}, {bg: C.redLight, text: C.red, label: '1 ★'}].map(l => (
                <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: l.bg, border: `1px solid ${l.text}33` }} />
                  <span style={{ fontSize: '10px', color: C.mid, fontWeight: 600 }}>{l.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Star filter pills */}
          <div style={{ padding: '0 14px 10px', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: C.mid, marginRight: '2px' }}>
              {selectedDay ? `${selectedDay} ${MONTHS_SHORT[month]}` : MONTHS_SHORT[month]}
            </span>
            <button
              onClick={() => setStarFilter(null)}
              style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', border: `1.5px solid ${starFilter === null ? C.navy : C.border}`, background: starFilter === null ? C.navy : C.white, color: starFilter === null ? C.white : C.mid }}
            >
              All ({monthReviews.length})
            </button>
            {starCounts.filter(s => s.count > 0).map(s => (
              <button
                key={s.star}
                onClick={() => setStarFilter(starFilter === s.star ? null : s.star)}
                style={{ padding: '3px 9px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', border: `1.5px solid ${starFilter === s.star ? C.amber : C.border}`, background: starFilter === s.star ? C.amberLight : C.white, color: starFilter === s.star ? C.amber : C.mid }}
              >
                {s.star}★ ({s.count})
              </button>
            ))}
          </div>

          {/* Review list */}
          <div style={{ padding: '0 14px 16px' }}>
            {displayReviews.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: C.mid, fontSize: '13px' }}>
                No reviews for this period
              </div>
            ) : (
              displayReviews
                .sort((a,b) => new Date(b.review_date) - new Date(a.review_date))
                .map(r => <ReviewCard key={r.id} review={r} compact />)
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

// ── Venue summary card ─────────────────────────────────────────────────────────
function VenueCard({ venue, reviews, loading, onOpen, isMobile }) {
  const vi = VENUE_IDENTITY[venue] || {};
  const now = new Date();

  // All-time — fetch accurate count + avg directly from DB
  const [allTimeCount, setAllTimeCount] = useState(null);
  const [allTimeAvg,   setAllTimeAvg]   = useState(null);
  useEffect(() => {
    async function fetchAggregate() {
      const { count } = await supabase
        .from('google_reviews')
        .select('id', { count: 'exact', head: true })
        .eq('venue', venue);
      setAllTimeCount(count ?? null);
      const { data } = await supabase
        .from('google_reviews')
        .select('star_rating')
        .eq('venue', venue)
        .limit(5000);
      if (data && data.length > 0) {
        setAllTimeAvg((Math.floor(data.reduce((s,r) => s + r.star_rating, 0) / data.length * 10) / 10).toFixed(1));
      }
    }
    fetchAggregate();
  }, [venue]);
  const displayAvg   = allTimeAvg;
  const displayCount = allTimeCount;

  // MTD
  const monthStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;
  const mtdReviews = reviews.filter(r => r.review_date.startsWith(monthStr));
  const mtdAvg = mtdReviews.length > 0
    ? (Math.floor(mtdReviews.reduce((s,r) => s + r.star_rating, 0) / mtdReviews.length * 10) / 10).toFixed(1)
    : null;
  const mtdFive = mtdReviews.filter(r => r.star_rating === 5).length;

  // LY MTD 5★
  const lyMonthStr = `${now.getFullYear()-1}-${String(now.getMonth()+1).padStart(2,'0')}`;
  const lyMtdDay   = now.getDate();
  const lyFive     = reviews.filter(r => {
    if (!r.review_date.startsWith(lyMonthStr)) return false;
    const d = new Date(r.review_date);
    return d.getDate() <= lyMtdDay && r.star_rating === 5;
  }).length;

  // Chart data
  const chartData = buildMonthlyData(reviews, now);

  return (
    <div
      onClick={() => onOpen(venue)}
      style={{
        background: C.white, border: `1.5px solid ${vi.border || C.border}`,
        borderRadius: '12px', padding: '12px', cursor: 'pointer',
        transition: 'box-shadow 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
    >
      {/* Venue name */}
      <div style={{ fontSize: '10px', fontWeight: 800, color: vi.text, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '6px' }}>
        {vi.short}
      </div>

      {loading ? (
        <div style={{ height: '60px', background: C.cream, borderRadius: '8px' }} />
      ) : (
        <>
          {/* Header row — full width, breathing room */}
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '8px' }}>
            {/* Left: all-time */}
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '5px' }}>
                <span style={{ fontSize: '26px', fontWeight: 900, color: C.charcoal, lineHeight: 1 }}>
                  {displayAvg ?? '—'}
                </span>
                <Stars rating={Math.round(Number(displayAvg))} size={12} />
              </div>
              <div style={{ fontSize: '10px', color: C.mid, fontWeight: 600, marginTop: '1px' }}>
                All time · {displayCount ?? '…'}
              </div>
            </div>
            {/* Right: two stats with shared month label above */}
            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-end' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '9px', fontWeight: 600, color: C.mid, marginBottom: '2px' }}>AVG</div>
                <div style={{ fontSize: '18px', fontWeight: 900, color: C.charcoal, lineHeight: 1 }}>{mtdAvg ?? '—'}</div>
                <div style={{ fontSize: '8px', color: C.mid, marginTop: '1px' }}>{MONTHS_SHORT[now.getMonth()]}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '9px', fontWeight: 600, color: C.mid, marginBottom: '2px' }}>5★</div>
                <div style={{ fontSize: '18px', fontWeight: 900, color: C.charcoal, lineHeight: 1 }}>{mtdFive}</div>
                {lyFive > 0
                  ? <div style={{ fontSize: '8px', fontWeight: 700, color: mtdFive >= lyFive ? C.green : C.red, marginTop: '1px', whiteSpace: 'nowrap' }}>{mtdFive >= lyFive ? '▲' : '▼'}{Math.abs(mtdFive - lyFive)} LY</div>
                  : <div style={{ fontSize: '8px', color: C.mid, marginTop: '1px' }}>{MONTHS_SHORT[now.getMonth()]}</div>
                }
              </div>
            </div>
          </div>

          {/* Chart — monthly FY, CY vs LY */}
          {chartData.length > 0 && (
            <>
              <div style={{ fontSize: '9px', color: C.mid, fontWeight: 600, marginBottom: '2px', display: 'flex', gap: '10px' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <span style={{ width: 10, height: 2, background: C.amber, display: 'inline-block', borderRadius: 1 }} />CY
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <span style={{ width: 10, height: 2, background: '#7A8A96', display: 'inline-block', borderRadius: 1 }} />LY
                </span>
              </div>
              <ResponsiveContainer width="100%" height={120}>
                <LineChart data={chartData} margin={{ top: 4, right: 6, left: 4, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 8, fill: C.mid }}
                    axisLine={false}
                    tickLine={false}
                    interval={1}
                  />
                  <YAxis
                    tick={{ fontSize: 8, fill: C.mid }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                    width={24}
                    tickCount={4}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '5px 9px', fontSize: '11px' }}>
                          <div style={{ color: C.mid, marginBottom: '2px', fontWeight: 700 }}>{label}</div>
                          <div style={{ color: C.amber, fontWeight: 700 }}>CY 5★: {payload[0]?.value ?? '—'}</div>
                          <div style={{ color: '#7A8A96', fontWeight: 600 }}>LY 5★: {payload[1]?.value ?? '—'}</div>
                        </div>
                      );
                    }}
                  />
                  <Line type="monotone" dataKey="cy" stroke={C.amber} strokeWidth={2} dot={false} connectNulls={false} />
                  <Line type="monotone" dataKey="ly" stroke="#7A8A96" strokeWidth={2} dot={false} strokeDasharray="5 3" />
                </LineChart>
              </ResponsiveContainer>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ── Desktop feed filters + paginated list ─────────────────────────────────────
function DesktopFeed({ reviews, loading }) {
  const [venueFilter, setVenueFilter] = useState('All');
  const [starFilter,  setStarFilter]  = useState(null);
  const [datePreset,  setDatePreset]  = useState('This Month');
  const [page,        setPage]        = useState(1);
  const PER_PAGE = 20;

  const now = new Date();
  const fmtLocal = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const todayStr = fmtLocal(now);

  function applyDatePreset(r) {
    const d = r.review_date.slice(0,10);
    if (datePreset === 'Today')      return d === todayStr;
    if (datePreset === 'This Week') {
      const weekStart = new Date(now);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
      return d >= fmtLocal(weekStart);
    }
    if (datePreset === 'This Month') return d.startsWith(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`);
    if (datePreset === 'This Year')  return d.startsWith(`${now.getFullYear()}`);
    return true; // All Time
  }

  const filtered = reviews
    .filter(r => venueFilter === 'All' || r.venue === venueFilter)
    .filter(r => starFilter === null || r.star_rating === starFilter)
    .filter(applyDatePreset)
    .sort((a,b) => new Date(b.review_date) - new Date(a.review_date));

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paged      = filtered.slice((page-1)*PER_PAGE, page*PER_PAGE);

  // Reset page on filter change
  const resetPage = () => setPage(1);

  return (
    <div style={{ marginTop: '24px' }}>
      {/* Filters */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '16px' }}>
        {/* Venue pills */}
        {['All', ...VENUES].map(v => {
          const vi = VENUE_IDENTITY[v] || {};
          const active = venueFilter === v;
          return (
            <button key={v} onClick={() => { setVenueFilter(v); resetPage(); }}
              style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', border: `1.5px solid ${active ? (vi.border || C.navy) : C.border}`, background: active ? (vi.bg || C.navy) : C.white, color: active ? (vi.text || C.white) : C.mid, transition: 'all 0.12s' }}>
              {v === 'All' ? 'All Venues' : (VENUE_IDENTITY[v]?.short || v)}
            </button>
          );
        })}

        <div style={{ width: '1px', height: '20px', background: C.border, margin: '0 2px' }} />

        {/* Star pills */}
        {[5,4,3,2,1].map(s => (
          <button key={s} onClick={() => { setStarFilter(starFilter === s ? null : s); resetPage(); }}
            style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', border: `1.5px solid ${starFilter === s ? C.amber : C.border}`, background: starFilter === s ? C.amberLight : C.white, color: starFilter === s ? C.amber : C.mid, transition: 'all 0.12s' }}>
            {s}★
          </button>
        ))}

        <div style={{ width: '1px', height: '20px', background: C.border, margin: '0 2px' }} />

        {/* Date presets */}
        {['Today','This Week','This Month','This Year','All Time'].map(p => (
          <button key={p} onClick={() => { setDatePreset(p); resetPage(); }}
            style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', border: `1.5px solid ${datePreset === p ? C.navy : C.border}`, background: datePreset === p ? C.navy : C.white, color: datePreset === p ? C.white : C.mid, transition: 'all 0.12s' }}>
            {p}
          </button>
        ))}
      </div>

      {/* Count */}
      <div style={{ fontSize: '12px', color: C.mid, marginBottom: '12px', fontWeight: 600 }}>
        {filtered.length} review{filtered.length !== 1 ? 's' : ''}
        {totalPages > 1 ? ` · page ${page} of ${totalPages}` : ''}
      </div>

      {/* Reviews */}
      {loading ? (
        Array.from({length: 5}).map((_,i) => (
          <div key={i} style={{ height: '80px', background: C.white, borderRadius: '10px', marginBottom: '8px', border: `1px solid ${C.border}` }} />
        ))
      ) : paged.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: C.mid, fontSize: '14px' }}>
          No reviews match these filters
        </div>
      ) : (
        paged.map(r => <ReviewCard key={r.id} review={r} />)
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
          <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}
            style={{ padding: '6px 14px', borderRadius: '8px', border: `1.5px solid ${C.border}`, background: C.white, color: page === 1 ? C.border : C.navy, fontWeight: 700, fontSize: '13px', cursor: page === 1 ? 'default' : 'pointer' }}>
            ‹ Prev
          </button>
          <span style={{ fontSize: '12px', color: C.mid, fontWeight: 600 }}>{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages}
            style={{ padding: '6px 14px', borderRadius: '8px', border: `1.5px solid ${C.border}`, background: C.white, color: page === totalPages ? C.border : C.navy, fontWeight: 700, fontSize: '13px', cursor: page === totalPages ? 'default' : 'pointer' }}>
            Next ›
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function ReviewsPage({ currentUser }) {
  const [reviews,     setReviews]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [activeVenue, setActiveVenue] = useState(null); // modal

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const isMobile = windowWidth < 768;

  useEffect(() => {
    async function load() {
      const { data, error } = await supabase
        .from('google_reviews')
        .select('*')
        .order('review_date', { ascending: false })
        .limit(5000);
      if (!error && data) setReviews(data);
      setLoading(false);
    }
    load();
  }, []);

  const venueReviews = (venue) => reviews.filter(r => r.venue === venue);

  return (
    <div style={{ padding: isMobile ? '1rem' : '1.5rem', maxWidth: '1200px', margin: '0 auto', fontFamily: 'system-ui, sans-serif' }}>

      {/* Page header */}
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? '1.25rem' : '1.5rem', fontWeight: 900, color: C.navy, letterSpacing: '-0.02em' }}>
          Reviews
        </h2>
        <p style={{ margin: 0, fontSize: '13px', color: C.mid }}>
          Google reviews across all venues
        </p>
      </div>

      {/* Venue cards */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '10px' }}>
        {VENUES.map(venue => (
          <VenueCard
            key={venue}
            venue={venue}
            reviews={venueReviews(venue)}
            loading={loading}
            onOpen={setActiveVenue}
            isMobile={isMobile}
          />
        ))}
      </div>

      {/* Desktop full feed */}
      {!isMobile && (
        <DesktopFeed reviews={reviews} loading={loading} />
      )}

      {/* Mobile: simple feed below cards */}
      {isMobile && (
        <div style={{ marginTop: '20px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
            Recent reviews
          </div>
          {loading ? (
            Array.from({length: 3}).map((_,i) => (
              <div key={i} style={{ height: '70px', background: C.white, borderRadius: '10px', marginBottom: '8px', border: `1px solid ${C.border}` }} />
            ))
          ) : (
            reviews.slice(0, 30).map(r => <ReviewCard key={r.id} review={r} />)
          )}
        </div>
      )}

      {/* Venue detail modal */}
      {activeVenue && (
        <VenueModal
          venue={activeVenue}
          reviews={venueReviews(activeVenue)}
          onClose={() => setActiveVenue(null)}
        />
      )}
    </div>
  );
}